/**
 * Utilities for handling script URLs - loading, saving, and sharing MethodScript code via URLs.
 * Supports both plain URL-encoded strings and LZ-string compressed data (like TypeScript Playground).
 */

import { logger } from 'shared-monaco-methodscript';

// @ts-expect-error - LZ-string wrapper is a vendor file without types
import LZStringImport from '../public/vendor/lz-string-wrapper.js';

import type { LZStringStatic } from './lz-string.js';

const LZString = LZStringImport as LZStringStatic;

/**
 * Sanitize code from querystring by filtering out non-ASCII characters except newlines
 * @param code - The code to sanitize
 * @returns The sanitized code
 */
export function sanitizeCodeFromQuerystring(code: string): string {
    if (!code || typeof code !== 'string') {
        return '';
    }

    // Filter out all non-ASCII characters except newline (\n)
    // ASCII printable range: 32-126, plus newline (10) and tab (9) for indentation
    return code
        .split('')
        .filter((char) => {
            const charCode = char.charCodeAt(0);
            // Allow printable ASCII (32-126), newline (10), and tab (9)
            return charCode === 10 || charCode === 9 || (charCode >= 32 && charCode <= 126);
        })
        .join('');
}

/**
 * Validation result for MethodScript format
 */
export type ValidationResult = {
    valid: boolean;
    error?: string;
};

/**
 * Validate that the script is in MethodSCRIPT format
 * MethodSCRIPT lines end with '\n', no trailing empty lines allowed.
 * @param script - The script to validate
 * @returns Validation result with `valid` boolean and `error` message if invalid
 */
export function validateMethodScriptFormat(script: string): ValidationResult {
    if (!script || typeof script !== 'string') {
        return { valid: false, error: 'Script is empty or invalid' };
    }

    // Rule 1: Must end with "\n" (last line must have trailing newline)
    if (!script.endsWith('\n')) {
        return { valid: false, error: 'Script must end with "\\n"' };
    }

    // Rule 2: Must not end with "\n\n" (no trailing empty lines)
    if (script.endsWith('\n\n')) {
        return { valid: false, error: 'Script must not have trailing empty lines' };
    }

    return { valid: true };
}

/**
 * Decode code from URL-encoded or LZ-compressed string
 * @param encodedCode - The encoded code string
 * @returns The decoded and sanitized code, or null if decoding fails
 */
export function decodeCodeFromUrl(encodedCode: string | null): string | null {
    if (!encodedCode) {
        return null;
    }

    try {
        // First, try to decompress with LZ-string (TypeScript Playground style)
        try {
            const decompressed = LZString.decompressFromEncodedURIComponent(encodedCode);
            if (decompressed) {
                return sanitizeCodeFromQuerystring(decompressed);
            }
        } catch {
            // Not LZ-compressed, try plain decoding
        }

        // Fall back to plain URL decoding (backward compatibility)
        const decodedCode = decodeURIComponent(encodedCode);
        // Sanitize to remove non-ASCII characters (except newlines)
        return sanitizeCodeFromQuerystring(decodedCode);
    } catch (error) {
        logger.error('Failed to decode code from URL:', error);
        return null;
    }
}

/**
 * Load script from querystring parameter 'code'
 * Supports both plain URL-encoded strings and LZ-string compressed data
 * @returns The decoded and sanitized script, or null if not present
 */
export function loadScriptFromQuerystring(): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    const encodedCode = urlParams.get('code');
    return decodeCodeFromUrl(encodedCode);
}

/**
 * Load script from URL hash parameter 'code'
 * Supports both plain URL-encoded strings and LZ-string compressed data
 * @returns The decoded and sanitized script, or null if not present
 */
export function loadScriptFromHash(): string | null {
    const hash = window.location.hash.substring(1); // Remove the '#'
    const hashParams = new URLSearchParams(hash);
    const codeFromHash = hashParams.get('code');
    return decodeCodeFromUrl(codeFromHash);
}

/**
 * Compress code for sharing via URL using LZ-string
 * @param code - The code to compress
 * @returns The compressed and URL-safe code string
 */
export function compressCodeForUrl(code: string): string {
    return LZString.compressToEncodedURIComponent(code);
}

/**
 * Create a shareable URL for the given code
 * @param code - The MethodScript code to share
 * @returns The full shareable URL with compressed code in hash
 */
export function createShareableUrl(code: string): string {
    const compressed = compressCodeForUrl(code);
    const baseUrl = window.location.origin + window.location.pathname;
    return `${baseUrl}#code=${compressed}`;
}
